import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  PopupRequest,
} from '@azure/msal-browser';
import { loginRequest, msalInstance } from 'authConfig';

let interactiveTokenRequest: Promise<string> | null = null;

const getAccount = (): AccountInfo | undefined =>
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

const storeActiveAccount = ({ account, accessToken }: AuthenticationResult) => {
  if (account) {
    msalInstance.setActiveAccount(account);
  }
  return accessToken;
};

const acquireTokenInteractively = (account?: AccountInfo): Promise<string> => {
  if (!interactiveTokenRequest) {
    const request: PopupRequest = {
      ...loginRequest,
      ...(account && { account }),
    };

    const authentication = account
      ? msalInstance.acquireTokenPopup(request)
      : msalInstance.loginPopup(request);

    interactiveTokenRequest = authentication
      .then(storeActiveAccount)
      .finally(() => {
        interactiveTokenRequest = null;
      });
  }

  return interactiveTokenRequest;
};

export const acquireAccessToken = async (forceRefresh = false) => {
  const account = getAccount();

  if (!account) {
    return acquireTokenInteractively();
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });
    return storeActiveAccount(response);
  } catch (error) {
    if (
      error instanceof InteractionRequiredAuthError ||
      (error as Error).name === 'InteractionRequiredAuthError'
    ) {
      return acquireTokenInteractively(account);
    }
    throw error;
  }
};

const withAuthorization = (init: RequestInit, accessToken: string) => {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return { ...init, headers };
};

export const fetchWithAuthorization = async (
  input: RequestInfo,
  init: RequestInit = {},
) => {
  const accessToken = await acquireAccessToken();
  let response = await fetch(input, withAuthorization(init, accessToken));

  if (response.status === 401) {
    const refreshedAccessToken = await acquireAccessToken(true);
    response = await fetch(
      input,
      withAuthorization(init, refreshedAccessToken),
    );
  }

  return response;
};
